import React, { useState, useContext } from 'react';
import { useThread } from '../context/ThreadContext';
import { AuthContext } from '../context/AuthContext';
import { FolderService } from '../services/FolderService';

const FolderList = () => {
  const { state, dispatch } = useThread();
  const { currentUser } = useContext(AuthContext);
  const { folders = [], threads = [], currentThread } = state;
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const folder = await FolderService.createFolder(currentUser.uid, newFolderName);
      dispatch({
        type: 'ADD_FOLDER',
        payload: folder
      });
      setNewFolderName('');
      setIsCreating(false);
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleThreadClick = (thread) => {
    dispatch({ type: 'SET_CURRENT_THREAD', payload: thread });
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const handleDrop = async (e, targetFolderId = null) => {
    e.preventDefault();
    const threadId = e.dataTransfer.getData('threadId');
    if (!threadId) return;

    try {
      // Remove from current folder first
      for (const folder of folders) {
        if (folder.threads?.includes(threadId)) {
          await FolderService.removeThreadFromFolder(folder.id, threadId);
        }
      }

      // If dropping into a new folder, add it
      if (targetFolderId) {
        await FolderService.addThreadToFolder(targetFolderId, threadId);
      }

      // Fetch updated folders
      const updatedFolders = await FolderService.getUserFolders(currentUser.uid);
      dispatch({ type: 'SET_FOLDERS', payload: updatedFolders });
    } catch (error) {
      console.error('Error moving thread:', error);
    }
  };

  const handleDeleteFolder = async (e, folderId) => {
    e.stopPropagation(); // Prevent folder toggle
    try {
      await FolderService.deleteFolder(folderId);
      const updatedFolders = await FolderService.getUserFolders(currentUser.uid);
      dispatch({ type: 'SET_FOLDERS', payload: updatedFolders });
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  return (
    <div className="folders-section">
      <div className="folder-header">
        <button 
          className="create-folder-btn"
          onClick={() => setIsCreating(true)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
          </svg>
          New Folder
        </button>
      </div>

      {isCreating && (
        <div className="new-folder-input">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name..."
            autoFocus
          />
          <button onClick={handleCreateFolder} title="Create folder">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button onClick={() => setIsCreating(false)} title="Cancel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      <div className="folders-list">
        {folders.map(folder => {
          const folderThreads = threads.filter(thread => 
            folder.threads?.includes(thread.id)
          );

          return (
            <div 
              key={folder.id} 
              className="folder-container"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, folder.id)}
            >
              <div 
                className="folder-header-item"
                onClick={() => toggleFolder(folder.id)}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="currentColor"
                  style={{ 
                    transform: expandedFolders[folder.id] ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path d="M6 12l4-4-4-4" />
                </svg>
                <div className="folder-name">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1.5 2h4.586a1 1 0 0 1 .707.293l1.414 1.414a1 1 0 0 0 .707.293H14.5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
                  </svg>
                  {folder.name}
                  <button
                    className="delete-folder-btn"
                    onClick={(e) => handleDeleteFolder(e, folder.id)}
                    title="Delete folder"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              </div>
              {expandedFolders[folder.id] && (
                <div className="folder-threads">
                  {folderThreads.map((thread) => (
                    <div
                      key={thread.id}
                      className={`thread-item folder-thread ${currentThread?.id === thread.id ? 'active' : ''}`}
                      onClick={() => handleThreadClick(thread)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('threadId', thread.id);
                      }}
                    >
                      <div className="thread-title">{thread.title}</div>
                      <div className="thread-preview">
                        {thread.messages[thread.messages.length - 1]?.content.substring(0, 30)}...
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FolderList; 